import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import Gap from '@component/common/gap';
import Spacer from '@component/common/spacer';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { getStudyRoute } from '@constant/iyagigo/variable';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import { IStudyRouteStep } from '@datatype/iyagigo/type';
import { useHomeWeekAttendance, useStudyConfiguration } from '@hook/iyagigo/useStudent';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as navigationActions } from '@store/slice/navigation-slice';
import ImageBlock from '@component/common/block/image-block';
import otterLogoImage from '@asset/image/otter-brick-logo.webp';

interface IReview {
  title: string;
  description: string;
  handleClick: () => void;
}

interface IServiceRoute {
  title: string;
  handleClick: () => void;
}

interface IProps {
  title: string;
  result: React.ReactNode;
  completedStepKey?: IStudyRouteStep['attendanceCheckKey'];
  review?: IReview;
  serviceRoute?: IServiceRoute;
  children?: React.ReactNode;
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${size.MEDIUM_MODAL_WIDTH};
`;

const ReviewContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  width: 100%;
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.WHITE_1};
  box-shadow: ${color.SURFACE_SHADOW};
  cursor: pointer;
`;

const ReviewTextContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  align-items: center;
`;

const StudyRouteContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const StudyRouteItem = styled.div<{ $recommended: boolean }>`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  width: 100%;
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_1};
  background-color: ${(props) => (props.$recommended ? color.BROWN_5 : color.GRAY_5)};
  cursor: pointer;
`;

const ServiceRouteContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  gap: ${size.MARGIN_1};
  width: 100%;
  margin: ${size.MARGIN_3} 0 0;
`;

const STUDY_ROUTE_TITLE: Record<IStudyRouteStep['attendanceCheckKey'], string> = {
  isCharacterCheck: '히라가나·가타카나 익히기',
  isClassroomExpressionCheck: '기본표현 익히기',
  isConversationCheck: '생활회화 익히기',
  isGrammarCheck: '문법 배우기',
  isClozeCheck: '빈칸문제 풀기',
  isVocabularyBookCheck: '단어집 외우기',
  isMiniStoryCheck: '간단한 이야기 읽기',
  isAuthenticExpressionCheck: '생생표현 익히기',
};

const StudyCompleteModalTemplate: React.FC<IProps> = ({
  title,
  result,
  completedStepKey,
  review,
  serviceRoute,
  children,
}) => {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const studyConfiguration = useStudyConfiguration();
  const todayAttendance = useHomeWeekAttendance().find((item) => item.isCurrent);
  const studyLanguage = studyConfiguration?.studyLanguage ?? EApiLanguageCode.JAPANESE;
  const languageLevel =
    studyLanguage === EApiLanguageCode.ENGLISH
      ? studyConfiguration?.englishLanguageLevel
      : studyConfiguration?.japaneseLanguageLevel;
  const remainingStudyRouteSteps = getStudyRoute(studyLanguage, languageLevel).filter(
    (step) =>
      (!completedStepKey || step.attendanceCheckKey !== completedStepKey) &&
      !todayAttendance?.[step.attendanceCheckKey],
  );

  const handleGoHomeClick = () => {
    dispatch(modalActions.hideModal());
    navigate(IYAGIGO_PATH.HOME_BEGIN_PATH);
  };

  const handleStudyClick = (step: IStudyRouteStep) => {
    dispatch(modalActions.hideModal());
    if (step.classroomSubMenu) {
      dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'classroom', subMenu: step.classroomSubMenu }));
    }
    if (step.clipSubMenu) {
      dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'clip', subMenu: step.clipSubMenu }));
    }
    navigate(step.path);
  };

  const StudyRouteItems = remainingStudyRouteSteps.map((step, index) => {
    const isRecommended = index === 0;
    return (
      <StudyRouteItem key={step.attendanceCheckKey} $recommended={isRecommended} onClick={() => handleStudyClick(step)}>
        <IconBlock
          icon='play'
          design={{
            icon: { size: 'tiny', color: color.GRAY_1, kind: 'filled' },
            box: {
              width: '20px',
              height: '20px',
              backgroundColor: color.WHITE_1,
              border: true,
              borderWidth: '1px',
              borderColor: color.GRAY_BOX_BORDER_COLOR,
              borderRadius: '50%',
            },
          }}
        />
        <Gap width={size.MARGIN_1} />
        <TextBlock
          text={STUDY_ROUTE_TITLE[step.attendanceCheckKey]}
          design={{
            text: { size: 'small', color: isRecommended ? color.BLACK_2 : color.GRAY_1, bold: isRecommended },
            box: { alignX: 'left' },
          }}
        />
        <Spacer />
        <IconBlock icon='right' design={{ icon: { size: 'tiny', color: color.GRAY_1, kind: 'outlined' }, box: {} }} />
      </StudyRouteItem>
    );
  });

  return (
    <Wrapper>
      <TextBlock text={title} design={{ text: { size: 'medium', color: color.BLACK_2, bold: true }, box: {} }} />
      <Gap height={size.MARGIN_1} />
      {result}
      {review && (
        <>
          <Gap height={size.MARGIN_1} />
          <ReviewContainer onClick={review.handleClick}>
            <ImageBlock
              src={otterLogoImage}
              alt='otterbrick'
              design={{
                box: { width: '50px' },
                image: { width: '50px' },
              }}
            />
            <Gap width={size.MARGIN_1} />
            <ReviewTextContainer>
              <TextBlock
                text={review.title}
                design={{
                  text: { size: 'small', color: color.BROWN_1, bold: true },
                  box: { alignX: 'left', width: '100%' },
                }}
              />
              <TextBlock
                text={review.description}
                design={{
                  text: { size: 'small', color: color.GRAY_1, bold: false },
                  box: { alignX: 'left', width: '100%' },
                }}
              />
            </ReviewTextContainer>
            <IconBlock
              icon='right'
              design={{ icon: { size: 'tiny', color: color.BROWN_1, kind: 'outlined' }, box: {} }}
            />
          </ReviewContainer>
        </>
      )}
      <Gap height={size.MARGIN_3} />
      {remainingStudyRouteSteps.length > 0 ? (
        <StudyRouteContainer>
          <TextBlock
            text='이어서 하면 좋아요'
            design={{ text: { size: 'small', color: color.BROWN_1, bold: true }, box: { alignX: 'left' } }}
          />
          {StudyRouteItems}
        </StudyRouteContainer>
      ) : (
        <TextBlock
          text='오늘의 학습을 모두 마쳤어요'
          design={{ text: { size: 'small', color: color.BROWN_1, bold: true }, box: { alignX: 'left' } }}
        />
      )}
      {children && (
        <>
          <Gap height={size.MARGIN_3} />
          {children}
        </>
      )}
      <ServiceRouteContainer>
        <TextBlock
          text='홈으로 돌아가기'
          design={{
            text: { size: 'small', color: color.BROWN_1, bold: false, underline: true },
            box: { event: { cursor: 'pointer' } },
          }}
          click={{ handler: handleGoHomeClick }}
        />
        {serviceRoute && (
          <TextBlock
            text={serviceRoute.title}
            design={{
              text: { size: 'small', color: color.BROWN_1, bold: false, underline: true },
              box: { event: { cursor: 'pointer' } },
            }}
            click={{ handler: serviceRoute.handleClick }}
          />
        )}
      </ServiceRouteContainer>
    </Wrapper>
  );
};

export default StudyCompleteModalTemplate;
